/**
 * DM state — conversations + messages + unread counts + compose drafts.
 *
 * Feed points:
 *   - `EventEnvelope::DmDelivery` → `appendMessage` (routes on
 *     `conversation_id`, bumps unread if the conversation is not active).
 *   - `ConversationUpdate` → `applyConversationUpdate` (member add/remove,
 *     rename, create/leave) — baseline stub.
 *
 * Send path:
 *   - Compose → `send(DmSend intent)` via the live-wire store. Client
 *     appends an optimistic local entry (`pending: true`) so the sender
 *     sees the message right away; the server echo (in offline dev, the
 *     WireMockServer's intent-echo fixture handler) replaces it with the
 *     canonical one on arrival.
 *
 * Pagination + edit + delete are staged behaviours — state shape here
 * supports them (messages carry edited/deleted flags, the store has no
 * inherent ordering constraint) but the corresponding UI + intent
 * wiring lands in a follow-on D10 fire.
 */

import { create } from 'zustand';

import type { PlayerId } from '../wire';

export interface Conversation {
  id: string;
  /** Display label; if unset, the UI synthesises "dm with @name". */
  name: string | null;
  /** Member PlayerIds — at least two (self + counterpart). */
  members: PlayerId[];
  /** Server tick at which the last message was delivered. Drives sort
   *  order (most-recent first). */
  last_message_at: bigint;
}

export interface DmMessage {
  /** Snowflake id — treated opaquely as a sortable numeric key. */
  id: string;
  conversation_id: string;
  sender: PlayerId;
  content: string;
  sent_at: bigint;
  edited: boolean;
  deleted: boolean;
  /** Local optimistic flag — replaced when the server echoes the
   *  canonical message. */
  pending?: boolean;
}

export interface DmState {
  conversations: Map<string, Conversation>;
  messages: Map<string, DmMessage[]>;
  unread: Record<string, number>;
  activeConversationId: string | null;
  composeText: string;

  // ─── Actions ─────────────────────────────────────────────────────────
  setActiveConversation: (id: string | null) => void;
  setComposeText: (text: string) => void;
  upsertConversation: (c: Conversation) => void;
  appendMessage: (msg: DmMessage) => void;
  appendOptimistic: (msg: DmMessage) => void;
  markRead: (id: string) => void;
  reset: () => void;
}

export const useDms = create<DmState>((set) => ({
  conversations: new Map(),
  messages: new Map(),
  unread: {},
  activeConversationId: null,
  composeText: '',

  setActiveConversation: (id) =>
    set((s) => ({
      activeConversationId: id,
      // Reading a conversation zeroes its unread.
      unread:
        id !== null ? clearKey(s.unread, id) : s.unread,
    })),

  setComposeText: (text) => set(() => ({ composeText: text })),

  upsertConversation: (c) =>
    set((s) => {
      const next = new Map(s.conversations);
      next.set(c.id, c);
      return { conversations: next };
    }),

  appendMessage: (msg) =>
    set((s) => {
      const list = s.messages.get(msg.conversation_id) ?? [];
      // If an optimistic message with the same content is in flight from
      // the same sender, replace it with the canonical one; otherwise append.
      const replaceIdx = list.findIndex(
        (m) =>
          m.pending === true &&
          m.sender === msg.sender &&
          m.content === msg.content,
      );
      const next =
        replaceIdx >= 0
          ? [
              ...list.slice(0, replaceIdx),
              msg,
              ...list.slice(replaceIdx + 1),
            ]
          : [...list, msg];
      const messages = new Map(s.messages);
      messages.set(msg.conversation_id, next);

      // Touch the conversation's last_message_at + unread-if-inactive.
      const conversations = new Map(s.conversations);
      const convo = conversations.get(msg.conversation_id);
      if (convo) {
        conversations.set(msg.conversation_id, {
          ...convo,
          last_message_at: msg.sent_at,
        });
      }
      const unread =
        s.activeConversationId === msg.conversation_id
          ? s.unread
          : bumpKey(s.unread, msg.conversation_id);

      return { messages, conversations, unread };
    }),

  appendOptimistic: (msg) =>
    set((s) => {
      const list = s.messages.get(msg.conversation_id) ?? [];
      const messages = new Map(s.messages);
      messages.set(msg.conversation_id, [...list, { ...msg, pending: true }]);
      return { messages };
    }),

  markRead: (id) =>
    set((s) => ({ unread: clearKey(s.unread, id) })),

  reset: () =>
    set(() => ({
      conversations: new Map(),
      messages: new Map(),
      unread: {},
      activeConversationId: null,
      composeText: '',
    })),
}));

function clearKey<V>(obj: Record<string, V>, key: string): Record<string, V> {
  if (!(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
}

function bumpKey(
  obj: Record<string, number>,
  key: string,
): Record<string, number> {
  return { ...obj, [key]: (obj[key] ?? 0) + 1 };
}

/** Selector: conversations ordered most-recent first. */
export function selectOrderedConversations(
  s: DmState,
): Conversation[] {
  return [...s.conversations.values()].sort((a, b) => {
    if (a.last_message_at === b.last_message_at) return 0;
    return a.last_message_at > b.last_message_at ? -1 : 1;
  });
}
